import React from "react";
import Calendar from "./Calendar";
import ReservationListCalendar from "./ReservationListCalendar";
import WaitingListCalendar from "./WaitingListCalendar";
import CancellationListCalendar from "./CancellationListCalendar";
import StaticReservationListCalendar from "./StaticReservationListCalendar";
import RequestListCalendar from "./RequestListCalendar";
import { StaticReservations } from "../models/StaticReservations";
import { ReservationSearch as ReservationList } from "../models/ReservationSearch";
import { CancellationSearch as CancellationList } from "../models/CancellationSearch";
import { RequestSearch as RequestList } from "../models/RequestSearch";
import { OrderSearch as OrderList } from "../models/OrderSearch";
import { WaitingListSearch as WaitingList } from "../models/WaitingListSearch";
import { ConflictSearch as ConflictList } from "../models/ConflictSearch";
import OrderListCalendar from "./OrderListCalendar";
import ConflictListCalendar from "./ConflictListCalendar";
import CalendarIcon from "./CalendarIcon";
import PrefsComponentCalendar from "./preferences/PrefsComponentCalendar";
import ExamComponentCalendar from "./exam/ExamComponentCalendar";
import MemberObjectCreatorCalendar from "./memberobjectcreator/MemberObjectCreatorCalendar";
import { MemberData as MemberCreator } from "../models/MemberData";
import { PrefsData } from "../models/PrefsData";
import { ExamData } from "../models/ExamData";
import _ from "underscore";
import { TAppContextProp, withAppContext } from "../contexts/AppContext";
import { TListCalendarWrapperProps } from "../types/components/ListCalendarWrapper";
import { Section as SectionModel } from "../models/Section";
import { Entry } from "../models/Entry";

const SIZE_MODIFIER = 30;

/**
 * App renders either Section or TabbedSection into the same element (see
 * SectionType in App.render), so both are handed the same props. Section
 * spreads them straight into the calendar wrappers; TabbedSection names each one
 * as it builds a single calendar per tab, which is why it has to enumerate them.
 *
 * The wrapper props cover everything the calendars themselves take; the rest is
 * what this component reads for the tab bar and the active calendar.
 */
type TTabbedSectionProps = Omit<TListCalendarWrapperProps<SectionModel>, "data" | "size"> & {
    /**
     * Both are optional on the wrapper props because the calendars can be
     * rendered without them; this component reads them on every render, exactly
     * as Section does.
     */
    data: SectionModel;
    size: { width: number; height: number };
    activeCalendarId: number;
    showCalendarIds: boolean;
    getCalendarIndex: (calendar: any) => number;
    onEntryInfoOpen: (entryIds: number[], showPanel?: boolean) => void;
    onOrderInfoOpen: (orderIds: number[], openIfClosed?: boolean) => void;
    /** Only compared in shouldComponentUpdate, then handed on with the props. */
    copiedEntry?: Entry;
    copiedGroupedEntries?: Entry[];
} & TAppContextProp;

class TabbedSection extends React.Component<TTabbedSectionProps> {
    setActiveCalendar = (id) => {
        this.props.onActiveCalendarChange?.(id);
    };

    getActiveCalendar = () => {
        if (!this.props.data) {
            return <div />;
        }
        const calendar = this.props.data.getAllChildren()[this.props.activeCalendarId];
        let size = this.props.size;
        if (this.props.data.getAllChildren().length > 1) {
            size = { width: size.width, height: size.height - SIZE_MODIFIER };
        }
        if (calendar instanceof ReservationList) {
            return (
                <ReservationListCalendar
                    flags={this.props.flags}
                    id={this.props.activeCalendarId}
                    size={size}
                    selection={this.props.selection}
                    data={calendar}
                    activeLayer={this.props.activeLayer}
                    massChangeEnabled={this.props.massChangeEnabled}
                    isMassChangeCopy={this.props.isMassChangeCopy}
                    setActiveLayer={this.props.setActiveLayer}
                    onLayerCreated={this.props.onLayerCreated}
                    getLayerName={this.props.getLayerName}
                    layers={this.props.layers}
                    showId={this.props.showCalendarIds}
                    isActive={true}
                    getCurrentFluffyItem={this.props.getCurrentFluffyItem}
                    toCalendar={this.toCalendar}
                    toPrefsComponent={this.toPrefsComponent}
                    toExamComponent={this.toExamComponent}
                    toMemberCreator={this.toMemberCreator}
                    toCancellationList={this.toCancellationList}
                    toOrderList={this.toOrderList}
                    toConflictList={this.toConflictList}
                    toWaitingList={this.toWaitingList}
                    toRequestList={this.toRequestList}
                    onActiveCalendarChange={this.props.onActiveCalendarChange}
                    onCalendarSwap={this.props.onCalendarSwap}
                    className="embeddedList"
                    style={this.props.size}
                    infoEntryReservationIds={this.props.infoEntryReservationIds}
                    onInfoOpen={this.props.onEntryInfoOpen}
                    onEditReservationExceptions={this.props.onEditReservationExceptions}
                />
            );
        } else if (calendar instanceof WaitingList) {
            return (
                <WaitingListCalendar
                    id={this.props.activeCalendarId}
                    size={size}
                    selection={this.props.selection}
                    data={calendar}
                    activeLayer={this.props.activeLayer}
                    massChangeEnabled={this.props.massChangeEnabled}
                    isMassChangeCopy={this.props.isMassChangeCopy}
                    setActiveLayer={this.props.setActiveLayer}
                    onLayerCreated={this.props.onLayerCreated}
                    getLayerName={this.props.getLayerName}
                    layers={this.props.layers}
                    showId={this.props.showCalendarIds}
                    isActive={true}
                    getCurrentFluffyItem={this.props.getCurrentFluffyItem}
                    toCalendar={this.toCalendar}
                    toPrefsComponent={this.toPrefsComponent}
                    toExamComponent={this.toExamComponent}
                    toMemberCreator={this.toMemberCreator}
                    toList={this.toList}
                    toCancellationList={this.toCancellationList}
                    toOrderList={this.toOrderList}
                    toConflictList={this.toConflictList}
                    toRequestList={this.toRequestList}
                    onActiveCalendarChange={this.props.onActiveCalendarChange}
                    onCalendarSwap={this.props.onCalendarSwap}
                    className="embeddedList"
                    style={this.props.size}
                    infoEntryReservationIds={this.props.infoEntryReservationIds}
                    onInfoOpen={this.props.onEntryInfoOpen}
                    onEditReservationExceptions={this.props.onEditReservationExceptions}
                />
            );
        } else if (calendar instanceof OrderList) {
            return (
                <OrderListCalendar
                    id={this.props.activeCalendarId}
                    size={size}
                    selection={this.props.selection}
                    data={calendar}
                    activeLayer={this.props.activeLayer}
                    massChangeEnabled={this.props.massChangeEnabled}
                    isMassChangeCopy={this.props.isMassChangeCopy}
                    setActiveLayer={this.props.setActiveLayer}
                    onLayerCreated={this.props.onLayerCreated}
                    getLayerName={this.props.getLayerName}
                    layers={this.props.layers}
                    showId={this.props.showCalendarIds}
                    isActive={true}
                    getCurrentFluffyItem={this.props.getCurrentFluffyItem}
                    toCalendar={this.toCalendar}
                    toPrefsComponent={this.toPrefsComponent}
                    toExamComponent={this.toExamComponent}
                    toMemberCreator={this.toMemberCreator}
                    toList={this.toList}
                    toCancellationList={this.toCancellationList}
                    toWaitingList={this.toWaitingList}
                    toRequestList={this.toRequestList}
                    toConflictList={this.toConflictList}
                    onActiveCalendarChange={this.props.onActiveCalendarChange}
                    onCalendarSwap={this.props.onCalendarSwap}
                    className="embeddedList"
                    style={this.props.size}
                    onInfoOpen={this.props.onOrderInfoOpen}
                />
            );
        } else if (calendar instanceof CancellationList) {
            return (
                <CancellationListCalendar
                    id={this.props.activeCalendarId}
                    size={size}
                    selection={this.props.selection}
                    data={calendar}
                    activeLayer={this.props.activeLayer}
                    massChangeEnabled={this.props.massChangeEnabled}
                    isMassChangeCopy={this.props.isMassChangeCopy}
                    setActiveLayer={this.props.setActiveLayer}
                    onLayerCreated={this.props.onLayerCreated}
                    getLayerName={this.props.getLayerName}
                    layers={this.props.layers}
                    showId={this.props.showCalendarIds}
                    isActive={true}
                    getCurrentFluffyItem={this.props.getCurrentFluffyItem}
                    toCalendar={this.toCalendar}
                    toPrefsComponent={this.toPrefsComponent}
                    toExamComponent={this.toExamComponent}
                    toMemberCreator={this.toMemberCreator}
                    toList={this.toList}
                    toOrderList={this.toOrderList}
                    toConflictList={this.toConflictList}
                    toWaitingList={this.toWaitingList}
                    toRequestList={this.toRequestList}
                    onActiveCalendarChange={this.props.onActiveCalendarChange}
                    onCalendarSwap={this.props.onCalendarSwap}
                    className="embeddedList"
                    style={this.props.size}
                    onInfoOpen={this.props.onEntryInfoOpen}
                    onEditReservationExceptions={this.props.onEditReservationExceptions}
                />
            );
        } else if (calendar instanceof RequestList) {
            return (
                <RequestListCalendar
                    id={this.props.activeCalendarId}
                    size={size}
                    selection={this.props.selection}
                    data={calendar}
                    activeLayer={this.props.activeLayer}
                    massChangeEnabled={this.props.massChangeEnabled}
                    isMassChangeCopy={this.props.isMassChangeCopy}
                    setActiveLayer={this.props.setActiveLayer}
                    onLayerCreated={this.props.onLayerCreated}
                    getLayerName={this.props.getLayerName}
                    layers={this.props.layers}
                    showId={this.props.showCalendarIds}
                    isActive={true}
                    getCurrentFluffyItem={this.props.getCurrentFluffyItem}
                    toCalendar={this.toCalendar}
                    toPrefsComponent={this.toPrefsComponent}
                    toExamComponent={this.toExamComponent}
                    toMemberCreator={this.toMemberCreator}
                    toList={this.toList}
                    toOrderList={this.toOrderList}
                    toConflictList={this.toConflictList}
                    toCancellationList={this.toCancellationList}
                    toWaitingList={this.toWaitingList}
                    onActiveCalendarChange={this.props.onActiveCalendarChange}
                    onCalendarSwap={this.props.onCalendarSwap}
                    className="embeddedList"
                    style={this.props.size}
                    onInfoOpen={this.props.onEntryInfoOpen}
                />
            );
        } else if (calendar instanceof StaticReservations) {
            return (
                <StaticReservationListCalendar
                    id={this.props.activeCalendarId}
                    size={size}
                    selection={this.props.selection}
                    data={calendar}
                    activeLayer={0}
                    massChangeEnabled={this.props.massChangeEnabled}
                    isMassChangeCopy={this.props.isMassChangeCopy}
                    setActiveLayer={this.props.setActiveLayer}
                    onLayerCreated={this.props.onLayerCreated}
                    getLayerName={this.props.getLayerName}
                    layers={this.props.layers}
                    showId={this.props.showCalendarIds}
                    isActive={true}
                    getCurrentFluffyItem={this.props.getCurrentFluffyItem}
                    toCalendar={this.toCalendar}
                    toPrefsComponent={this.toPrefsComponent}
                    toExamComponent={this.toExamComponent}
                    toMemberCreator={this.toMemberCreator}
                    toCancellationList={this.toCancellationList}
                    toOrderList={this.toOrderList}
                    toConflictList={this.toConflictList}
                    toWaitingList={this.toWaitingList}
                    toRequestList={this.toRequestList}
                    onActiveCalendarChange={this.props.onActiveCalendarChange}
                    onCalendarSwap={this.props.onCalendarSwap}
                    className="embeddedList"
                    style={this.props.size}
                    infoEntryReservationIds={this.props.infoEntryReservationIds}
                    onInfoOpen={this.props.onEntryInfoOpen}
                    reservationIds={calendar.reservationIds}
                />
            );
        } else if (calendar instanceof PrefsData) {
            return (
                <PrefsComponentCalendar
                    className="embeddedList"
                    data={calendar}
                    key={this.props.activeCalendarId}
                    id={this.props.activeCalendarId}
                    style={this.props.size}
                    toCalendar={this.toCalendar}
                    toList={this.toList}
                    toCancellationList={this.toCancellationList}
                    toOrderList={this.toOrderList}
                    toConflictList={this.toConflictList}
                    toWaitingList={this.toWaitingList}
                    toRequestList={this.toRequestList}
                    toPrefsComponent={this.toPrefsComponent}
                    toExamComponent={this.toExamComponent}
                    toMemberCreator={this.toMemberCreator}
                    onActiveCalendarChange={this.props.onActiveCalendarChange}
                    onCalendarSwap={this.props.onCalendarSwap}
                />
            );
        } else if (calendar instanceof ExamData) {
            return (
                <ExamComponentCalendar
                    className="embeddedList"
                    data={calendar}
                    key={this.props.activeCalendarId}
                    id={this.props.activeCalendarId}
                    style={this.props.size}
                    toCalendar={this.toCalendar}
                    toList={this.toList}
                    toCancellationList={this.toCancellationList}
                    toOrderList={this.toOrderList}
                    toConflictList={this.toConflictList}
                    toWaitingList={this.toWaitingList}
                    toRequestList={this.toRequestList}
                    toPrefsComponent={this.toPrefsComponent}
                    toExamComponent={this.toExamComponent}
                    toMemberCreator={this.toMemberCreator}
                    onActiveCalendarChange={this.props.onActiveCalendarChange}
                    onCalendarSwap={this.props.onCalendarSwap}
                />
            );
        } else if (calendar instanceof ConflictList) {
            return (
                <ConflictListCalendar
                    id={this.props.activeCalendarId}
                    size={size}
                    selection={this.props.selection}
                    data={calendar}
                    activeLayer={this.props.activeLayer}
                    massChangeEnabled={this.props.massChangeEnabled}
                    isMassChangeCopy={this.props.isMassChangeCopy}
                    setActiveLayer={this.props.setActiveLayer}
                    onLayerCreated={this.props.onLayerCreated}
                    getLayerName={this.props.getLayerName}
                    layers={this.props.layers}
                    showId={this.props.showCalendarIds}
                    isActive={true}
                    getCurrentFluffyItem={this.props.getCurrentFluffyItem}
                    toCalendar={this.toCalendar}
                    toPrefsComponent={this.toPrefsComponent}
                    toExamComponent={this.toExamComponent}
                    toMemberCreator={this.toMemberCreator}
                    toList={this.toList}
                    toOrderList={this.toOrderList}
                    toCancellationList={this.toCancellationList}
                    toWaitingList={this.toWaitingList}
                    toRequestList={this.toRequestList}
                    onActiveCalendarChange={this.props.onActiveCalendarChange}
                    onCalendarSwap={this.props.onCalendarSwap}
                    className="embeddedList"
                    style={this.props.size}
                    onInfoOpen={this.props.onEntryInfoOpen}
                />
            );
        } else if (calendar instanceof MemberCreator) {
            return (
                <MemberObjectCreatorCalendar
                    {...this.props}
                    className="embeddedList"
                    data={calendar}
                    key={this.props.activeCalendarId}
                    id={this.props.activeCalendarId}
                    style={this.props.size}
                    toCalendar={this.toCalendar}
                    toList={this.toList}
                    toCancellationList={this.toCancellationList}
                    toOrderList={this.toOrderList}
                    toConflictList={this.toConflictList}
                    toWaitingList={this.toWaitingList}
                    toRequestList={this.toRequestList}
                    toPrefsComponent={this.toPrefsComponent}
                    toExamComponent={this.toExamComponent}
                    toMemberCreator={this.toMemberCreator}
                    onActiveCalendarChange={this.props.onActiveCalendarChange}
                    onCalendarSwap={this.props.onCalendarSwap}
                />
            );
        }
        return (
            <Calendar
                {...this.props}
                data={calendar}
                id={this.props.activeCalendarId}
                size={size}
                toList={this.toList}
                toCancellationList={this.toCancellationList}
                toOrderList={this.toOrderList}
                toConflictList={this.toConflictList}
                toWaitingList={this.toWaitingList}
                toRequestList={this.toRequestList}
                toPrefsComponent={this.toPrefsComponent}
                toExamComponent={this.toExamComponent}
                toMemberCreator={this.toMemberCreator}
                showId={this.props.showCalendarIds}
                isActive={true}
                onEditReservationExceptions={this.props.onEditReservationExceptions}
            />
        );
    };

    getActiveSection = () => {
        const calendar = this.props.data.getAllChildren()[this.props.activeCalendarId];
        return this.props.data.getCalendarSection(calendar);
    };

    toList = () => {
        const section = this.getActiveSection();
        this.props.appContext.update(section, section.toList(this.props.selection));
    };

    toCancellationList = () => {
        const section = this.getActiveSection();
        this.props.appContext.update(section, section.toCancellationList(this.props.selection));
    };

    toOrderList = () => {
        const section = this.getActiveSection();
        this.props.appContext.update(section, section.toOrderList(this.props.selection));
    };

    toWaitingList = () => {
        const section = this.getActiveSection();
        this.props.appContext.update(section, section.toWaitingList(this.props.selection));
    };

    toRequestList = () => {
        const section = this.getActiveSection();
        this.props.appContext.update(section, section.toRequestList(this.props.selection));
    };

    toConflictList = () => {
        const section = this.getActiveSection();
        this.props.appContext.update(section, section.toConflictList(this.props.selection));
    };

    toCalendar = () => {
        const section = this.getActiveSection();
        this.props.appContext.update(section, section.toCalendar(this.props.selection));
    };

    toPrefsComponent = () => {
        const section = this.getActiveSection();
        this.props.appContext.update(section, section.toPrefsComponent(this.props.selection));
    };

    toExamComponent = () => {
        this.props.appContext.clearExamRequest(() => {
            const section = this.getActiveSection();
            this.props.appContext.update(section, section.toExamComponent(this.props.selection));
        });
    };

    toMemberCreator = () => {
        const section = this.getActiveSection();
        this.props.appContext.update(section, section.toMemberCreator(this.props.selection));
    };

    render() {
        let calendarTabs: React.ReactElement | null = null;
        const calendars = this.props.data.getAllChildren();
        if (calendars.length > 1) {
            calendarTabs = (
                <ul className="calendarList">
                    {calendars.map((calendar, index) => {
                        let cssClass = "";
                        if (index === this.props.activeCalendarId) {
                            cssClass = "active";
                        }
                        return (
                            <li
                                key={index}
                                className={cssClass}
                                onClick={this.setActiveCalendar.bind(this, index)}
                            >
                                <CalendarIcon
                                    calendar={calendar}
                                    size={24}
                                    isActive={index === this.props.activeCalendarId}
                                />
                            </li>
                        );
                    }, this)}
                </ul>
            );
        }
        return (
            <div className="tabbedSection" style={this.props.size}>
                {calendarTabs}

                {this.getActiveCalendar()}
            </div>
        );
    }

    shouldComponentUpdate(nextProps) {
        return (
            nextProps.data !== this.props.data ||
            nextProps.size.width !== this.props.size.width ||
            nextProps.size.height !== this.props.size.height ||
            nextProps.selection !== this.props.selection ||
            nextProps.showCalendarIds !== this.props.showCalendarIds ||
            nextProps.getCalendarIndex !== this.props.getCalendarIndex ||
            nextProps.onActiveCalendarChange !== this.props.onActiveCalendarChange ||
            nextProps.activeCalendarId !== this.props.activeCalendarId ||
            nextProps.copiedEntry !== this.props.copiedEntry ||
            !_.isEqual(nextProps.copiedGroupedEntries, this.props.copiedGroupedEntries)
        );
    }
}

export default withAppContext(TabbedSection);
